"use client";

import React from "react";
import { useRouter } from "next/router";
import Carousel from "react-multi-carousel";
import { ButtonGroupProps } from "react-multi-carousel/lib/types";
import Image from "@/components/Atoms/Image";
import "react-multi-carousel/lib/styles.css";
import { routerHandler } from "@/lib/routes/router-links-handler";
import styles from "./index.module.scss";
import { Chevron } from "@/components/Atoms/Vectors";
import { MultiPurposeTitle } from "@/components/Atoms/Title";
import { ButtonMaster } from "@/atoms/Buttons";
import { SliderCard } from "@/components/Molecules/SliderCard";
import { getPortfolioSlug, pageRelatedLink, resolveProductImage } from "@/lib/functions";

type SectionSliderProps = {
  content?: any[];
  title?: string;
  useSectionMargins?: boolean;
  addSliderNavigationArrows?: boolean;
  sliderNavigationColor?: { color?: { hex?: string } };
  sliderCardCallToActionStyle?: string;
  callToActionTextColor?: { color?: { hex?: string } };
  callToActionStyleColor?: { color?: { hex?: string } };
  callToActionCustomIcon?: any;
  sliderCardLayout?: string;
  cardColor?: { color?: { hex?: string } };
  cardTextColor?: { color?: { hex?: string } };
  sliderNavigationArrowColors?: { hex?: string };
  addTitleUnderline?: boolean;
  underlineColor?: { color?: { hex?: string } };
  customUnderlineAsset?: any;
  sectionBackgroundColor?: { color?: { hex?: string } };
  pageRelated?: any[];
  [key: string]: any;
};

const HeroSliderNav = ({
  next,
  previous,
  sliderNavigationColor,
  addSliderNavigationArrows,
}: ButtonGroupProps & {
  sliderNavigationColor?: { color?: { hex?: string } };
  addSliderNavigationArrows?: boolean;
}) => {
  if (!addSliderNavigationArrows) return null;

  return (
    <div className="absolute justify-between mx-auto w-[90%] left-0 right-0 flex flex-row gap-12">
      <button
        type="button"
        className="flex items-center justify-center scale-[-2.5]"
        onClick={() => previous?.()}
        aria-label="Previous slide"
      >
        <Chevron color={sliderNavigationColor?.color} />
      </button>
      <button
        type="button"
        className="flex items-center justify-center scale-[2.5]"
        onClick={() => next?.()}
        aria-label="Next slide"
      >
        <Chevron color={sliderNavigationColor?.color} />
      </button>
    </div>
  );
};

export default function SectionSlider(data: SectionSliderProps) {
  const { locale }: any = useRouter();

  const responsive = {
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 1,
      partialVisibilityGutter: 100,
    },
    tablet: {
      breakpoint: { max: 1024, min: 464 },
      items: 1,
      partialVisibilityGutter: 100,
    },
    mobile: {
      breakpoint: { max: 464, min: 0 },
      items: 1,
      partialVisibilityGutter: 10,
    },
  };

  const portfolioSlug = getPortfolioSlug(data.pageRelated?.[0]?.page, locale);

  return (
    <div style={{ backgroundColor: data.sectionBackgroundColor?.color?.hex }}>
      <div
        className={`overflow-hidden ${
          data.useSectionMargins ? "py-8 md:py-20" : "py-0"
        } `}
      >
        <MultiPurposeTitle
          tag="h3"
          type="default"
          layout={"center"}
          data={data.title}
          color={data.cardTextColor?.color}
        />
        <div className="relative max-w-[1000px] my-12 mx-auto">
          <Carousel
            containerClass={`${styles.Slider}`}
            responsive={responsive}
            itemClass="h-full"
            arrows={false}
            centerMode={true}
            infinite={true}
            sliderClass={styles.SliderTrack}
            minimumTouchDrag={10}
            renderButtonGroupOutside
            customButtonGroup={
              <HeroSliderNav
                addSliderNavigationArrows={data.addSliderNavigationArrows}
                sliderNavigationColor={data.sliderNavigationColor}
              />
            }
          >
            {data.content?.map((entry: any, index: number) => (
              <div
                key={index}
                className={`${styles.activeDiv} h-full relative`}
              >
                {entry && (
                  <>
                    <Image
                      className={`${styles.sliderImage} z-30 mx-auto h-[33rem] hidden md:flex`}
                      data={resolveProductImage(entry)}
                    />
                    <SliderCard
                      title={entry?.productName}
                      description={entry?.productDescriptionShort}
                      image={resolveProductImage(entry)}
                      pageRelated={[
                        entry.slug,
                        data.sliderCardCallToActionStyle,
                        data.callToActionTextColor?.color,
                        data.callToActionStyleColor?.color,
                        entry.__typename,
                        locale,
                        entry.productTypeSpecific,
                        data.callToActionCustomIcon,
                      ]}
                      portfolioSlug={portfolioSlug}
                      card={[
                        data.sliderCardLayout,
                        data.cardColor?.color,
                        data.cardTextColor?.color,
                      ]}
                      navArrow={[
                        data.addSliderNavigationArrows,
                        data.sliderNavigationArrowColors,
                      ]}
                      related={data.pageRelated}
                      underline={!!data.addTitleUnderline}
                      underlineColor={data.underlineColor?.color}
                      customUnderline={data.customUnderlineAsset}
                    />
                  </>
                )}
              </div>
            ))}
          </Carousel>
        </div>
        {data.pageRelated?.map((entry: any, i: number) => (
          <div key={i} className="flex flex-row justify-center ">
            <ButtonMaster
              anim={""}
              type={entry?.buttonStyle}
              cta={entry?.typeColor?.color}
              color={entry?.labelColor?.color}
              customIcon={entry?.icon?.img}
              customColor={entry?.iconColor?.color}
              target={`${entry?.page ? `_self` : `_blank`}`}
              link={` ${
                entry?.page
                  ? ` ${routerHandler({
                      typename: entry?.__typename,
                      slug: pageRelatedLink(
                        entry?.page,
                        locale,
                        entry?.sectionAnchor?.title
                      ),
                      locale: locale,
                    })}`
                  : `${entry?.externalUrl}`
              }`}
            >
              {entry?.buttonLabel?.label}
            </ButtonMaster>
          </div>
        ))}
      </div>
    </div>
  );
}

export const fragment = `
    fragment SliderFragment on SectionSliderRecord{
        __typename
        id
        name
        title
        subtitle
        content {
            productName
            productDescriptionShort
            productDescription
            slug
            productTypeSpecific {
              __typename
              slug
            }
            factSheetImage {
              responsiveImage (imgixParams: { fit: crop, h: 700, crop: focalpoint, auto:format, q:70}) {
                ... responsiveImageFragment
              }
            }
            sku {
              skuName
              skuImageGallery {
                responsiveImage(imgixParams: {h: "500", fit: clamp, crop: focalpoint, auto: format, q: "65", ar: "1:1"}) {
                    ...responsiveImageFragment
                 }
                }
            }
         }
         useSectionMargins
        sliderCardLayout
        sliderCardCallToActionStyle
        addSliderNavigationArrows
        addSliderCardCallToAction
        addTitleUnderline
          underlineColor{
            color{
              hex
            }
          }
          cardColor{
            color{
              hex
            }
          }
          cardTextColor{
            color{
              hex
            }
          }
          customUnderlineAsset {
            img {
              url
              width
              height
            }
          }
          callToActionTextColor{
            color{
              hex
            }
          }
          callToActionStyleColor{
            color{
              hex
            }
          }
          callToActionCustomIcon{
            img{
              url
              width
              height
            }
          }
          sliderNavigationColor{
            color{
              hex
            }
          }
          sectionBackgroundColor{
            color{
              hex
            }
          }
          pageRelated {
            ...pageRelatedFragment
          }

  }
`;
